import './AccountInfoConfig';
import { useReactiveVar } from '@apollo/client';
import { useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ProfileContainer, ProfileDetails, BoxAvatar, EmptyMajorsLinkStyled } from './AccountInfoStyles';
import { userMajors } from '../../../cache/cache';
import { studentAcademicProgramsReactiveVar } from '../../../cache/learner/studentAcademicProgramsReactiveVar';
import AtomGrid from '../../../components/atoms/AtomGrid';
import AtomTypography from '../../../components/atoms/AtomTypography/AtomTypography';
import SkeletonComponent from '../../../components/generics/SkeletonComponent/SkeletonComponent';
import { styleConfigs } from '../../../components/resources/styleConfigs';
import { FeatureFlagsContext } from '../../../contexts/FeatureFlagsContext';
import userAuth from '../../../hooks/userAuth';
import { PATH_DASHBOARD_STUDENT } from '../../../routes/paths';
import palette from '../../../theme/learnerPalette';

const AccountInfo: React.FC = () => {
  const { auth } = userAuth();
  const theme = useTheme();
  const userMajorsStore = useReactiveVar(userMajors);
  const studentInitials = `${auth?.student?.name.replace(/\s/g, '')[0]}${
    auth?.student?.lastName.replace(/\s/g, '')[0]
  }`;
  const studentFullName = `${auth?.student?.name} ${auth?.student?.lastName}`;
  const navigate = useNavigate();
  const { t } = useTranslation('LearnerAccountInfo');
  const institutionName = auth?.studentInstitution?.institutionName ?? auth?.studentInstitution?.otherInstitutionName;
  const { features } = useContext(FeatureFlagsContext);
  const academicPrograms = useReactiveVar(studentAcademicProgramsReactiveVar);
  const majorsEnabled = features?.qsemMajorsEnableSpecializedMajors === true;

  return (
    <ProfileContainer>
      <AtomGrid container spacing={0.5}>
        <AtomGrid item xs={5}>
          <BoxAvatar>
            <AtomTypography color={theme.palette.primary.contrastText} variant="h2">
              {studentInitials}
            </AtomTypography>
          </BoxAvatar>
        </AtomGrid>
        <AtomGrid item xs={7}>
          <ProfileDetails>
            <AtomTypography
              component="p"
              fontFamily={styleConfigs.fonts.fontFamily}
              fontWeight={700}
              fontSize={styleConfigs.fonts.fontSize1rem}
              color={palette.light.text.black}
            >
              {studentFullName ?? t('GUEST_DISPLAYNAME')}
            </AtomTypography>
            {majorsEnabled ? (
              <>
                {academicPrograms?.data ? (
                  academicPrograms?.data.map((academicProgram, index: number) => (
                    <AtomTypography
                      key={index.toString()}
                      component="p"
                      fontFamily={styleConfigs.fonts.fontFamily}
                      fontWeight={400}
                      fontSize={styleConfigs.fonts.fontSize0p9rem}
                      color={palette.light.text.mistBlue}
                      sx={{ marginTop: `${styleConfigs.marginPadding.customNegative2}px` }}
                      data-testid={`profile-academic-program-${index}`}
                    >
                      {academicProgram?.name}
                    </AtomTypography>
                  ))
                ) : (
                  <SkeletonComponent sizeSkeleton={1} variant="h3" />
                )}
                {!academicPrograms?.data?.length && (
                  <AtomTypography
                    component="p"
                    fontFamily={styleConfigs.fonts.fontFamily}
                    fontWeight={400}
                    fontSize={styleConfigs.fonts.fontSize0p9rem}
                    color={palette.light.text.mistBlue}
                    sx={{ marginTop: `${styleConfigs.marginPadding.customNegative2}px` }}
                    data-testid="profile-no-academic-programs"
                  >
                    {t('NO_ACADEMIC_PROGRAMS')}
                  </AtomTypography>
                )}
              </>
            ) : (
              <>
                {userMajorsStore ? (
                  userMajorsStore.map((major, index: number) => (
                    <AtomTypography
                      key={index.toString()}
                      component="p"
                      fontFamily={styleConfigs.fonts.fontFamily}
                      fontWeight={400}
                      fontSize={styleConfigs.fonts.fontSize0p9rem}
                      color={palette.light.text.mistBlue}
                      sx={{ marginTop: `${styleConfigs.marginPadding.customNegative2}px` }}
                    >
                      {major.name}
                    </AtomTypography>
                  ))
                ) : (
                  <SkeletonComponent sizeSkeleton={1} variant="h3" />
                )}
                {!userMajorsStore?.length && (
                  <AtomTypography
                    component="p"
                    fontFamily={styleConfigs.fonts.fontFamily}
                    fontWeight={400}
                    fontSize={styleConfigs.fonts.fontSize0p9rem}
                    color={palette.light.text.mistBlue}
                    sx={{ marginTop: `${styleConfigs.marginPadding.customNegative2}px` }}
                  >
                    {t('NO_MAJORS_LENGTH')}
                  </AtomTypography>
                )}
              </>
            )}
            {institutionName && (
              <>
                <AtomTypography
                  component="p"
                  fontFamily={styleConfigs.fonts.fontFamily}
                  fontWeight={400}
                  fontSize={styleConfigs.fonts.fontSize0p7rem}
                  color={palette.light.text.mistBlue}
                  sx={{ marginTop: styleConfigs.marginPadding.custom10rem }}
                >
                  {t('INFO_POPOVER_STUDENT_LOCATION')}
                </AtomTypography>
                <AtomTypography
                  component="p"
                  fontFamily={styleConfigs.fonts.fontFamily}
                  fontWeight={400}
                  fontSize={styleConfigs.fonts.fontSize0p9rem}
                  color={palette.light.text.black}
                >
                  {institutionName}
                </AtomTypography>
              </>
            )}
            {!userMajorsStore?.length && !academicPrograms?.data?.length && (
              <EmptyMajorsLinkStyled onClick={() => navigate(PATH_DASHBOARD_STUDENT.profile.information)}>
                {!majorsEnabled ? t('ADD_MAJOR') : t('ADD_ACADEMIC_PROGRAM')}
              </EmptyMajorsLinkStyled>
            )}
          </ProfileDetails>
        </AtomGrid>
      </AtomGrid>
    </ProfileContainer>
  );
};

export default AccountInfo;
